(() => {
  const TAG_NAME = 'optiversal-related-links';
  const PAGE_TYPE_PRIORITY = { plp: 0, opt: 1 };
  const UNKNOWN_PAGE_TYPE_PRIORITY = 2;

  if (customElements.get(TAG_NAME)) return;

  class OptiversalRelatedLinks extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') return;

      this.dataset.loaded = 'true';
      this.load();
    }

    async load() {
      const content = this.querySelector('[data-optiversal-content]');
      const list = this.querySelector('[data-optiversal-list]');
      const template = this.querySelector('template[data-optiversal-link-template]');
      const status = this.querySelector('[data-optiversal-status]');

      if (!content || !list || !template) return;

      const endpoint = this.dataset.endpoint;
      const organizationId = this.dataset.organizationId;
      const queryType = this.dataset.queryType;
      const query = this.dataset.query;

      if (!endpoint || !organizationId || !queryType) return;
      if (!query && queryType !== 'random' && queryType !== 'newest') return;

      const requestUrl = new URL(endpoint);
      requestUrl.searchParams.set('o', organizationId);
      requestUrl.searchParams.set('queryType', queryType);
      requestUrl.searchParams.set('max', '9');

      if (query) requestUrl.searchParams.set('query', query);
      if (this.dataset.exclude) requestUrl.searchParams.set('exclude', this.dataset.exclude);
      if (this.dataset.pageTypes) requestUrl.searchParams.set('pageTypes', this.dataset.pageTypes);

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 7000);

      this.setAttribute('aria-busy', 'true');
      if (status) status.textContent = 'Loading related links';

      try {
        const response = await fetch(requestUrl, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Optiversal returned HTTP ${response.status}`);
        }

        const payload = await response.json();
        const results = Array.isArray(payload?.results) ? payload.results : [];
        const orderedResults = this.orderResultsByPageType(results);
        const seenUrls = new Set();

        orderedResults.forEach((result) => {
          const url = this.safeHttpUrl(result?.url);
          const title = typeof result?.title === 'string' ? result.title.trim() : '';

          if (!url || !title || this.isCurrentPage(url) || seenUrls.has(url.href)) return;
          seenUrls.add(url.href);

          const item = template.content.cloneNode(true);
          const anchor = item.querySelector('[data-optiversal-link]');
          const titleElement = item.querySelector('[data-optiversal-title]');
          const image = item.querySelector('[data-optiversal-image]');
          const imageWrapper = item.querySelector('[data-optiversal-image-wrapper]');

          anchor.href = url.href;
          titleElement.textContent = title;

          if (image && imageWrapper && this.dataset.showImages === 'true') {
            const imageUrl = this.safeHttpUrl(result?.imageUrl);
            if (imageUrl) {
              image.src = imageUrl.href;
              image.alt = '';
              imageWrapper.hidden = false;
            }
          }

          list.appendChild(item);
        });

        if (seenUrls.size > 0) {
          content.hidden = false;
          if (status) status.textContent = `${seenUrls.size} related links loaded`;
        } else if (status) {
          status.textContent = 'No related links available';
        }
      } catch (error) {
        if (status) status.textContent = 'Related links could not be loaded';
        console.warn('[Optiversal related links]', error);
      } finally {
        window.clearTimeout(timeout);
        this.removeAttribute('aria-busy');
      }
    }

    pageTypeTokens(value) {
      if (Array.isArray(value)) {
        return value
          .filter((entry) => typeof entry === 'string')
          .map((entry) => entry.trim().toLowerCase())
          .filter(Boolean);
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map((entry) => entry.trim().toLowerCase())
          .filter(Boolean);
      }

      return [];
    }

    pageTypePriority(result) {
      // The API returns an item's type as a string (`"plp"`) and, for items that
      // belong to more than one list, as an array (`["plp"]`, also exposed as
      // `allPageTypes`). Unknown or missing types sort after the known ones.
      const tokens = [
        ...this.pageTypeTokens(result?.pageTypes),
        ...this.pageTypeTokens(result?.pageType),
        ...this.pageTypeTokens(result?.allPageTypes),
      ];

      let priority = UNKNOWN_PAGE_TYPE_PRIORITY;

      tokens.forEach((token) => {
        const candidate = PAGE_TYPE_PRIORITY[token];
        if (typeof candidate === 'number' && candidate < priority) priority = candidate;
      });

      return priority;
    }

    orderResultsByPageType(results) {
      // Stable grouping: PLP first, then opt, then anything unexpected, keeping
      // the API's original relevance order inside each group.
      return results
        .map((result, index) => ({ result, index, priority: this.pageTypePriority(result) }))
        .sort((a, b) => a.priority - b.priority || a.index - b.index)
        .map((entry) => entry.result);
    }

    safeHttpUrl(value) {
      if (typeof value !== 'string' || !value.trim()) return null;

      try {
        const url = new URL(value, window.location.origin);
        return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
      } catch (_error) {
        return null;
      }
    }

    isCurrentPage(url) {
      const normalizeHost = (host) => host.toLowerCase().replace(/^www\./, '');
      const normalizePath = (path) => path.replace(/\/+$/, '') || '/';

      return (
        normalizeHost(url.hostname) === normalizeHost(window.location.hostname) &&
        normalizePath(url.pathname) === normalizePath(window.location.pathname)
      );
    }
  }

  customElements.define(TAG_NAME, OptiversalRelatedLinks);
})();
